/**
 * Date utility functions for consistent date handling across the app.
 *
 * Architecture:
 * - Backend stores dates as YYYY-MM-DD (no timezone, just dates)
 * - API client converts to Date objects using `new Date(string)`
 * - This creates midnight UTC, which can shift to previous day in local timezone
 * - Solution: Always format dates in local timezone, ignoring UTC conversion
 */

const DATE_ONLY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseDateOnlyParts = (
  dateOnly: string,
): [number, number, number] | null => {
  const match = DATE_ONLY_REGEX.exec(dateOnly.trim());
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day)
  ) {
    return null;
  }

  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }

  const probe = new Date(year, month - 1, day, 12, 0, 0, 0);
  if (
    probe.getFullYear() !== year ||
    probe.getMonth() !== month - 1 ||
    probe.getDate() !== day
  ) {
    return null;
  }

  return [year, month, day];
};

/**
 * Format a Date object to YYYY-MM-DD string in local timezone.
 * Use this when converting user-selected dates (from calendar/date pickers) to strings.
 */
export const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Parses YYYY-MM-DD into a local Date at noon to avoid timezone day rollover.
 */
export const parseDateOnlyToLocalDate = (dateOnly: string): Date => {
  const parts = parseDateOnlyParts(dateOnly);
  if (!parts) return new Date(Number.NaN);

  const [year, month, day] = parts;
  return new Date(year, month - 1, day, 12, 0, 0, 0);
};

/**
 * Converts a local Date into YYYY-MM-DD based on local calendar fields.
 */
export const localDateToDateOnly = (date: Date): string => {
  return formatDateLocal(date);
};

/**
 * Converts YYYY-MM-DD into a UTC-noon Date to keep serialized API day stable.
 */
export const dateOnlyToApiDate = (dateOnly: string): Date | undefined => {
  const parts = parseDateOnlyParts(dateOnly);
  if (!parts) return undefined;

  const [year, month, day] = parts;
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0));
};

/**
 * Normalizes API date input (Date or string) to YYYY-MM-DD.
 */
export const apiDateToDateOnly = (
  input: Date | string | null | undefined,
): string => {
  if (!input) return "";

  if (typeof input === "string") {
    const trimmed = input.trim();
    const prefix = trimmed.slice(0, 10);
    if (DATE_ONLY_REGEX.test(prefix)) {
      return prefix;
    }

    const parsed = new Date(trimmed);
    return localDateToDateOnly(parsed);
  }

  return formatDateFromApi(input);
};

/**
 * Format a Date object from the API to YYYY-MM-DD string using UTC components.
 * Use this when the Date object was created from an API date string like "2025-10-21".
 *
 * Why? The API returns "2025-10-21" which becomes midnight UTC when parsed.
 * In PST/PDT, this shifts to Oct 20 at 4pm/5pm, so getDate() returns 20.
 * We need to extract the UTC components to get the original date (21).
 */
export const formatDateFromApi = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Create a Date object from date components in UTC at noon.
 * This prevents timezone conversion issues when sending to API.
 *
 * Why noon UTC? Because toISOString().substring(0,10) will always give us
 * the correct date regardless of local timezone.
 */
export const createDateUTC = (date: Date): Date => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  return new Date(Date.UTC(year, month, day, 12, 0, 0));
};

/**
 * Format a date from the API for display.
 * API returns dates as strings like "2025-10-19" which the API client
 * converts to Date objects. This function formats them correctly for display.
 *
 * @param date - Date object from API (created from YYYY-MM-DD string), or null/undefined
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string in local timezone, or empty string if date is null/undefined
 */
export const formatDateForDisplay = (
  date: Date | string | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  },
): string => {
  // Handle null/undefined dates
  if (!date) {
    return "";
  }

  // If it's already a string in YYYY-MM-DD format, parse it manually
  if (typeof date === "string") {
    const [year, month, day] = date.split("-").map(Number);
    // Create date in local timezone (not UTC)
    const localDate = new Date(year, month - 1, day);
    return localDate.toLocaleDateString("en-US", options);
  }

  // If it's a Date object from API, it was created from "YYYY-MM-DD"
  // which makes it midnight UTC. We need to get the original date components.
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();

  // Create new date in local timezone with those components
  const localDate = new Date(year, month, day);
  return localDate.toLocaleDateString("en-US", options);
};

/**
 * Format a date for display in a specific format.
 * Convenience wrapper around formatDateForDisplay.
 */
export const formatDateLong = (
  date: Date | string | null | undefined,
): string => {
  if (!date) return "";
  return formatDateForDisplay(date, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

/**
 * Format a date for display in short format.
 */
export const formatDateShort = (
  date: Date | string | null | undefined,
): string => {
  if (!date) return "";
  return formatDateForDisplay(date, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Check if a date is in the past (compared to today at midnight).
 */
export const isPastDate = (date: Date): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate < today;
};

/**
 * Get current date in a specific timezone (or local timezone if not specified).
 * Returns a Date object representing "today" at midnight in the specified timezone.
 *
 * @param timezone - IANA timezone string (e.g., "America/New_York", "Europe/London")
 * @returns Date object representing today at midnight in the specified timezone
 */
export const getTodayInTimezone = (timezone?: string): Date => {
  const now = new Date();

  if (!timezone) {
    // Use local timezone
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return today;
  }

  try {
    // Get current date/time in the user's timezone
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    const parts = formatter.formatToParts(now);
    const year = parseInt(parts.find((p) => p.type === "year")?.value || "0");
    const month =
      parseInt(parts.find((p) => p.type === "month")?.value || "0") - 1;
    const day = parseInt(parts.find((p) => p.type === "day")?.value || "0");

    // Create date at midnight in local timezone
    return new Date(year, month, day);
  } catch {
    // If timezone is invalid, fall back to local timezone
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return today;
  }
};

/**
 * Check if a date string (YYYY-MM-DD) is today or in the past, considering user's timezone.
 *
 * @param date - Date string in YYYY-MM-DD format (from API) or Date object
 * @param timezone - User's IANA timezone string
 * @returns true if the date is today or in the past
 */
export const isDateTodayOrPast = (
  date: string | Date,
  timezone?: string,
): boolean => {
  let targetDate: Date;

  if (typeof date === "string") {
    // Parse the date string to get the date components
    const [year, month, day] = date.split("-").map(Number);
    targetDate = new Date(year, month - 1, day);
  } else {
    // If it's a Date object from API, extract UTC components to get original date
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate();
    targetDate = new Date(year, month, day);
  }

  // Get today in the user's timezone
  const today = getTodayInTimezone(timezone);

  // Compare dates (both at midnight)
  return targetDate <= today;
};

/**
 * Check if a date string (YYYY-MM-DD) is in the future, considering user's timezone.
 *
 * @param date - Date string in YYYY-MM-DD format (from API) or Date object
 * @param timezone - User's IANA timezone string
 * @returns true if the date is in the future
 */
export const isDateInFuture = (
  date: string | Date,
  timezone?: string,
): boolean => {
  return !isDateTodayOrPast(date, timezone);
};
